import {
  useAccountUsers,
  useCreateTeam,
  useDeleteTeam,
  useSitesWithMembers,
  useTeams,
  useUpdateTeam,
} from '@my/directory-api/hooks';
import type {
  CreateTeamRequest,
  Team,
  TeamMemberSummary,
  UpdateTeamRequest,
  User,
} from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  Loading,
  SelectInput,
  TextInput,
  showToast,
} from '@my/ui';
import {
  Check,
  Edit,
  Globe,
  MapPin,
  Plus,
  Trash2,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface TeamsTabProps {
  /** Owning organisation id, resolved by the parent page from JWT claims. */
  organisationId: string;
}

/**
 * Teams tab — master-detail with the editor on the left and the user
 * roster on the right. The right panel adapts to the current mode:
 *
 *   - **No selection**: empty state
 *   - **Viewing**: read-only list of the selected team's current members
 *   - **Editing**: every user in the org listed with a checkbox bound to
 *     the editor's draft. Toggling on the right updates the same state
 *     the Save button on the left reads from.
 *
 * The editor itself replaces the selected team's card in the left column
 * (or appears at the top for a new team) — keeps the spatial mapping
 * "card → form → card" intuitive while leaving the cards above/below
 * still visible for context.
 */
export function TeamsTab({ organisationId }: TeamsTabProps) {
  const { t } = useTranslation('organisation');

  const teamsQuery = useTeams(organisationId, 'Check');
  const sitesQuery = useSitesWithMembers(organisationId);
  const usersQuery = useAccountUsers();
  const createMutation = useCreateTeam();
  const updateMutation = useUpdateTeam();
  const deleteMutation = useDeleteTeam();

  // `null` = no team selected; `'new'` = creating; guid = viewing/editing that team.
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<TeamForm>(emptyForm());
  const [submitted, setSubmitted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Team | null>(null);

  const isBusy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Stable refs so the lookup memos don't churn on every render.
  const teams = useMemo(() => teamsQuery.data ?? [], [teamsQuery.data]);
  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);

  const siteOptions = useMemo(
    () => [
      { value: '', label: t('teams.crossSite') },
      ...sites.map((s) => ({ value: s.id, label: s.name })),
    ],
    [sites, t],
  );

  const siteNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sites) map.set(s.id, s.name);
    return map;
  }, [sites]);

  // Members picker source — every user with a Check grant on this org.
  const memberCandidates = useMemo<User[]>(() => {
    const all = usersQuery.data ?? [];
    return all
      .filter((u) =>
        u.accounts.some((a) =>
          a.appRoles.some(
            (r) => r.app === 'Check' && r.organisationId === organisationId,
          ),
        ),
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [usersQuery.data, organisationId]);

  // Team memberships are org-wide in the Directory (UserTeam has no app
  // dimension), so `team.members` includes users assigned via other apps.
  // For display we scope rosters + count badges to users who hold a Check
  // grant; the full member list still seeds the edit draft so a save
  // preserves non-Check assignments rather than dropping them.
  const checkUserIds = useMemo(
    () => new Set(memberCandidates.map((u) => u.id)),
    [memberCandidates],
  );

  const selectedTeam = useMemo<Team | null>(() => {
    if (selectedId === null || selectedId === 'new') return null;
    return teams.find((t) => t.id === selectedId) ?? null;
  }, [selectedId, teams]);

  // If the selected team disappears (delete from another tab, server
  // refresh, etc.), fall back to empty state cleanly.
  useEffect(() => {
    if (selectedId === null || selectedId === 'new') return;
    if (!teams.some((t) => t.id === selectedId)) {
      setSelectedId(null);
      setIsEditing(false);
    }
  }, [teams, selectedId]);

  // ── Action handlers ──────────────────────────────────────────────────
  const selectTeam = (team: Team) => {
    if (isEditing) return; // single-editor rule — discard-on-cancel pattern
    setSelectedId(team.id);
    setDraft(toForm(team));
    setSubmitted(false);
  };

  const startNew = () => {
    setSelectedId('new');
    setIsEditing(true);
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const startEdit = () => {
    if (!selectedTeam) return;
    setIsEditing(true);
    setDraft(toForm(selectedTeam));
    setSubmitted(false);
  };

  const cancelEdit = () => {
    if (selectedId === 'new') {
      setSelectedId(null);
    }
    setIsEditing(false);
    setSubmitted(false);
  };

  const handleSave = async () => {
    if (selectedId === null) return;
    setSubmitted(true);
    if (!isFormValid(draft)) return;

    try {
      if (selectedId === 'new') {
        const body: CreateTeamRequest = {
          organisationId,
          app: 'Check',
          name: draft.name.trim(),
          description: draft.description.trim() || null,
          siteId: draft.siteId || null,
          memberUserIds: draft.memberUserIds,
        };
        const newId = await createMutation.mutateAsync(body);
        showToast({ type: 'success', message: t('teams.added') });
        setSelectedId(newId);
        setIsEditing(false);
      } else {
        const body: UpdateTeamRequest = {
          teamId: selectedId,
          name: draft.name.trim(),
          description: draft.description.trim() || null,
          siteId: draft.siteId || null,
          memberUserIds: draft.memberUserIds,
        };
        await updateMutation.mutateAsync({ organisationId, app: 'Check', body });
        showToast({ type: 'success', message: t('teams.updated') });
        setIsEditing(false);
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('teams.saveFailed'),
      });
    }
  };

  const requestDelete = () => {
    if (!selectedTeam) return;
    setPendingDelete(selectedTeam);
  };

  const confirmDeletePending = async () => {
    const team = pendingDelete;
    if (!team) return;
    try {
      await deleteMutation.mutateAsync({ organisationId, app: 'Check', id: team.id });
      showToast({ type: 'success', message: t('teams.removed') });
      if (selectedId === team.id) {
        setSelectedId(null);
        setIsEditing(false);
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('teams.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  const toggleMember = (userId: string) => {
    setDraft((prev) => ({
      ...prev,
      memberUserIds: prev.memberUserIds.includes(userId)
        ? prev.memberUserIds.filter((id) => id !== userId)
        : [...prev.memberUserIds, userId],
    }));
  };

  return (
    <section className="rounded-md border border-slate-200 bg-white">
      {/* Title is intentionally omitted — the active sub-nav entry on the
          left already names the section. The description sits inline with
          the action area so the header reads as one row of context +
          actions. */}
      <header className="flex items-center justify-between gap-3 border-b border-slate-200 p-4">
        <p className="text-xs text-slate-500">{t('teams.description')}</p>
        {/* Hide + Add while an editor is open — single-editor rule.
            Disable while loading or while a mutation is in flight. */}
        {!isEditing && (
          <button
            type="button"
            aria-label={t('teams.addAria')}
            onClick={startNew}
            disabled={isBusy || teamsQuery.isPending}
            className="flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('teams.add')}
          </button>
        )}
      </header>

      {teamsQuery.isPending && (
        <div className="p-4">
          <Loading label={t('teams.loading')} />
        </div>
      )}
      {teamsQuery.isError && (
        <div className="p-4">
          <ErrorBanner
            title={t('teams.loadFailed')}
            message={teamsQuery.error.message}
          />
        </div>
      )}

      {teamsQuery.isSuccess && (
        <div className="grid min-h-[480px] grid-cols-1 lg:grid-cols-[560px_1fr]">
          <LeftColumn
            teams={teams}
            checkUserIds={checkUserIds}
            selectedId={selectedId}
            isEditing={isEditing}
            draft={draft}
            onDraftChange={setDraft}
            submitted={submitted}
            siteOptions={siteOptions}
            siteNameById={siteNameById}
            onSelect={selectTeam}
            onStartEdit={startEdit}
            onSave={handleSave}
            onCancel={cancelEdit}
            onRequestDelete={requestDelete}
            isBusy={isBusy}
          />
          <RightColumn
            selectedId={selectedId}
            selectedTeam={selectedTeam}
            checkUserIds={checkUserIds}
            isEditing={isEditing}
            memberCandidates={memberCandidates}
            selectedMemberIds={draft.memberUserIds}
            onToggleMember={toggleMember}
          />
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('teams.deleteDialog.title')}
        message={
          pendingDelete
            ? t('teams.deleteDialog.message', { name: pendingDelete.name })
            : null
        }
        confirmLabel={t('teams.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Left column: cards + inline editor
// ────────────────────────────────────────────────────────────────────────────

interface LeftColumnProps {
  teams: Team[];
  /** Users with a Check grant — scopes the per-card member count badge. */
  checkUserIds: Set<string>;
  selectedId: string | 'new' | null;
  isEditing: boolean;
  draft: TeamForm;
  onDraftChange: (next: TeamForm) => void;
  submitted: boolean;
  siteOptions: { value: string; label: string }[];
  siteNameById: Map<string, string>;
  onSelect: (team: Team) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onRequestDelete: () => void;
  isBusy: boolean;
}

function LeftColumn({
  teams,
  checkUserIds,
  selectedId,
  isEditing,
  draft,
  onDraftChange,
  submitted,
  siteOptions,
  siteNameById,
  onSelect,
  onStartEdit,
  onSave,
  onCancel,
  onRequestDelete,
  isBusy,
}: LeftColumnProps) {
  const { t } = useTranslation('organisation');
  const isNewMode = selectedId === 'new';

  return (
    <div className="border-b border-slate-200 lg:border-b-0 lg:border-r">
      {/*
        View mode: cap height + inner scroll so a long team list doesn't
        push the rest of the page down. Edit mode: drop the cap so the
        editor displays in full — the container grows downward into the
        empty space already reserved by the page layout instead of
        nesting a scrollbar inside the panel.
      */}
      <ul
        className={
          'space-y-2 p-3 ' +
          (isEditing
            ? ''
            : 'max-h-[calc(60vh+80px)] overflow-auto')
        }
      >
        {/* New-team editor at the top — matches the convention used on
            BankAccounts / TaxSchemas / Users grids. */}
        {isNewMode && isEditing && (
          <li>
            <TeamEditor
              isNew
              draft={draft}
              onChange={onDraftChange}
              submitted={submitted}
              siteOptions={siteOptions}
              onSave={onSave}
              onCancel={onCancel}
              isBusy={isBusy}
            />
          </li>
        )}

        {teams.length === 0 && !isNewMode && (
          <li>
            <p className="px-1 text-sm text-slate-500">{t('teams.empty')}</p>
          </li>
        )}

        {teams.map((team) => {
          const isSelected = selectedId === team.id;
          if (isSelected && isEditing) {
            // Replace the card slot with the inline editor.
            return (
              <li key={team.id}>
                <TeamEditor
                  isNew={false}
                  draft={draft}
                  onChange={onDraftChange}
                  submitted={submitted}
                  siteOptions={siteOptions}
                  onSave={onSave}
                  onCancel={onCancel}
                  onDelete={onRequestDelete}
                  isBusy={isBusy}
                />
              </li>
            );
          }
          return (
            <li key={team.id}>
              <TeamCard
                team={team}
                checkUserIds={checkUserIds}
                isSelected={isSelected}
                disabled={isEditing}
                siteName={team.siteName ?? siteNameById.get(team.siteId ?? '') ?? null}
                onSelect={() => onSelect(team)}
                onStartEdit={onStartEdit}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface TeamCardProps {
  team: Team;
  /** Users with a Check grant — scopes the member count badge. */
  checkUserIds: Set<string>;
  isSelected: boolean;
  disabled: boolean;
  siteName: string | null;
  onSelect: () => void;
  onStartEdit: () => void;
}

function TeamCard({
  team,
  checkUserIds,
  isSelected,
  disabled,
  siteName,
  onSelect,
  onStartEdit,
}: TeamCardProps) {
  const { t } = useTranslation('organisation');
  const checkMemberCount = team.members.filter((m) =>
    checkUserIds.has(m.userId),
  ).length;
  return (
    <div
      className={
        'relative w-full rounded-md border p-3 text-left transition-colors ' +
        (disabled
          ? 'border-slate-200 bg-white opacity-50'
          : isSelected
            ? 'border-purple-300 bg-purple-50 shadow-sm'
            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50')
      }
    >
      <div className="flex items-start gap-3">
        {/* Left side: clickable region with name + site + description.
            The site badge sits inline with the title (next to the name)
            so the available width stays usable on narrow screens. The
            description slot is ALWAYS rendered (non-breaking space when
            empty) and clamped to a fixed two-line height so cards don't
            reflow when description is absent — the colour/border swap on
            select is the only signal of state change. */}
        <button
          type="button"
          onClick={onSelect}
          disabled={disabled}
          className="block min-w-0 flex-1 text-left disabled:cursor-not-allowed"
        >
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-semibold text-slate-900">
              {team.name}
            </span>
            <SiteBadge siteId={team.siteId ?? null} siteName={siteName} />
          </div>
          <p
            className={
              'mt-1 line-clamp-2 min-h-[2lh] text-[11px] ' +
              (team.description
                ? 'text-slate-500'
                : 'select-none text-transparent')
            }
          >
            {team.description || ' '}
          </p>
        </button>
        {/* Right side: member count on top, edit pencil underneath. The
            edit-button slot is always reserved (24x24 placeholder when
            not selected) so cards keep a constant height across
            selection states. */}
        <div className="flex shrink-0 flex-col items-end gap-1">
          <MemberCountChip count={checkMemberCount} />
          {isSelected && !disabled ? (
            <button
              type="button"
              aria-label={t('teams.ariaEdit', { name: team.name })}
              onClick={(e) => {
                e.stopPropagation();
                onStartEdit();
              }}
              className="flex h-6 w-6 items-center justify-center rounded-sm bg-white/80 text-blue-600 ring-1 ring-inset ring-slate-200 hover:bg-white"
            >
              <Edit className="h-3 w-3" />
            </button>
          ) : (
            <div className="h-6 w-6" aria-hidden />
          )}
        </div>
      </div>
    </div>
  );
}

function MemberCountChip({ count }: { count: number }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
      <UsersIcon className="h-3 w-3" />
      {count}
    </span>
  );
}

function SiteBadge({
  siteId,
  siteName,
}: {
  siteId: string | null;
  siteName: string | null;
}) {
  const { t } = useTranslation('organisation');
  if (!siteId) {
    return (
      <span className="inline-flex items-center gap-1 rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium italic text-slate-600 ring-1 ring-inset ring-slate-200">
        <Globe className="h-3 w-3" />
        {t('teams.crossSiteShort')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-sm bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
      <MapPin className="h-3 w-3" />
      {siteName ?? '—'}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Right column: members view OR full users picker
// ────────────────────────────────────────────────────────────────────────────

interface RightColumnProps {
  selectedId: string | 'new' | null;
  selectedTeam: Team | null;
  /** Users with a Check grant — scopes the read-only roster. */
  checkUserIds: Set<string>;
  isEditing: boolean;
  memberCandidates: User[];
  selectedMemberIds: string[];
  onToggleMember: (userId: string) => void;
}

function RightColumn({
  selectedId,
  selectedTeam,
  checkUserIds,
  isEditing,
  memberCandidates,
  selectedMemberIds,
  onToggleMember,
}: RightColumnProps) {
  const { t } = useTranslation('organisation');

  // Empty — no team selected, no editor open.
  if (selectedId === null) {
    return (
      <div className="flex items-center justify-center p-8 text-center">
        <div>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <UsersIcon className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-slate-700">
            {t('teams.detail.emptyTitle')}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {t('teams.detail.emptyHint')}
          </p>
        </div>
      </div>
    );
  }

  // Edit mode (existing or new) — full users picker on the right.
  if (isEditing) {
    return (
      <MemberPicker
        candidates={memberCandidates}
        selected={selectedMemberIds}
        onToggle={onToggleMember}
      />
    );
  }

  // View mode — read-only roster of the selected team.
  if (!selectedTeam) {
    return (
      <div className="p-4">
        <Loading label={t('teams.loading')} />
      </div>
    );
  }
  // Scope the roster to Check users — team memberships are org-wide, so
  // `selectedTeam.members` includes users assigned via other apps.
  const visibleMembers = selectedTeam.members.filter((m) =>
    checkUserIds.has(m.userId),
  );
  return (
    <div className="flex flex-col p-4">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        {t('teams.detail.membersHeader', { count: visibleMembers.length })}
      </h3>
      {visibleMembers.length === 0 ? (
        <p className="text-xs text-slate-500">{t('teams.fields.noMembers')}</p>
      ) : (
        <ul className="max-h-[60vh] space-y-1 overflow-auto">
          {visibleMembers.map((m) => (
            <MemberRow key={m.userId} member={m} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MemberRow({ member }: { member: TeamMemberSummary }) {
  return (
    <li className="flex items-center gap-3 rounded-sm border border-slate-200 px-3 py-2">
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700"
        aria-hidden
      >
        {member.initials}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-slate-900">
          {member.fullName}
        </p>
        <p className="truncate text-xs text-slate-500">{member.email}</p>
      </div>
    </li>
  );
}

interface MemberPickerProps {
  candidates: User[];
  selected: string[];
  onToggle: (userId: string) => void;
}

/**
 * Edit-mode right panel: every user with a Depot grant on this org listed
 * with a checkbox. Toggling fires the parent's `onToggleMember`, which
 * mutates the same draft state the Save button on the left reads from.
 */
function MemberPicker({ candidates, selected, onToggle }: MemberPickerProps) {
  const { t } = useTranslation('organisation');

  if (candidates.length === 0) {
    return (
      <div className="p-4">
        <p className="text-xs text-slate-500">{t('teams.fields.noMembers')}</p>
      </div>
    );
  }

  const selectedSet = new Set(selected);
  // `selected` (the edit draft) retains member ids assigned via other apps
  // so a save preserves them — but they aren't rendered as candidates here,
  // so the "selected" count must only tally the visible (Check) candidates.
  const selectedVisibleCount = candidates.filter((c) => selectedSet.has(c.id)).length;

  return (
    <div className="flex flex-col p-4">
      <h3 className="mb-2 flex items-center justify-between text-xs font-medium uppercase tracking-wide text-slate-500">
        <span>
          {t('teams.detail.pickerHeader', { count: candidates.length })}
        </span>
        <span className="text-[10px] normal-case text-slate-400">
          {t('teams.detail.pickerSelected', { count: selectedVisibleCount })}
        </span>
      </h3>
      <ul className="max-h-[60vh] space-y-1 overflow-auto">
        {candidates.map((u) => {
          const checked = selectedSet.has(u.id);
          return (
            <li key={u.id}>
              <label
                className={
                  'flex items-center gap-3 rounded-sm border px-3 py-2 transition-colors cursor-pointer ' +
                  (checked
                    ? 'border-purple-300 bg-purple-50'
                    : 'border-slate-200 bg-white hover:bg-slate-50')
                }
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(u.id)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700"
                  aria-hidden
                >
                  {(u.initials || u.email.slice(0, 2)).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {u.fullName || u.email}
                  </p>
                  <p className="truncate text-xs text-slate-500">{u.email}</p>
                </div>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Editor (left column, in-place)
// ────────────────────────────────────────────────────────────────────────────

interface TeamForm {
  name: string;
  description: string;
  /** Empty string = cross-site. */
  siteId: string;
  memberUserIds: string[];
}

function emptyForm(): TeamForm {
  return { name: '', description: '', siteId: '', memberUserIds: [] };
}

function toForm(team: Team): TeamForm {
  return {
    name: team.name,
    description: team.description ?? '',
    siteId: team.siteId ?? '',
    memberUserIds: team.members.map((m) => m.userId),
  };
}

function isFormValid(form: TeamForm): boolean {
  return form.name.trim().length > 0 && form.name.length <= 64;
}

interface TeamEditorProps {
  isNew: boolean;
  draft: TeamForm;
  onChange: (next: TeamForm) => void;
  submitted: boolean;
  siteOptions: { value: string; label: string }[];
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isBusy: boolean;
}

function TeamEditor({
  isNew,
  draft,
  onChange,
  submitted,
  siteOptions,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: TeamEditorProps) {
  const { t } = useTranslation('organisation');
  const set = <K extends keyof TeamForm>(key: K, value: TeamForm[K]) =>
    onChange({ ...draft, [key]: value });

  const errs = { name: draft.name.trim().length === 0 };

  return (
    <div className="space-y-3 rounded-md border border-purple-200 bg-purple-50/40 p-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-slate-700">
        {isNew ? t('teams.detail.newTitle') : t('teams.detail.editTitle')}
      </h3>

      <TextInput
        label={t('teams.fields.name')}
        name="name"
        autoComplete="off"
        value={draft.name}
        maxLength={64}
        placeholder={t('teams.fields.namePlaceholder')}
        onChange={(e) => set('name', e.target.value)}
        error={submitted && errs.name}
        errorMessage={t('teams.validation.nameRequired')}
      />

      <TextInput
        label={t('teams.fields.description')}
        name="description"
        autoComplete="off"
        value={draft.description}
        maxLength={256}
        placeholder={t('teams.fields.descriptionPlaceholder')}
        onChange={(e) => set('description', e.target.value)}
      />

      <SelectInput
        label={t('teams.fields.site')}
        name="siteId"
        value={draft.siteId}
        onChange={(e) => set('siteId', e.target.value)}
        options={siteOptions}
      />

      <p className="text-[11px] text-slate-500">
        {t('teams.detail.editHint')}
      </p>

      <div className="flex items-center justify-between gap-1">
        <div>
          {onDelete && (
            <button
              type="button"
              aria-label={t('teams.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('teams.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('teams.ariaSave')}
            onClick={onSave}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
